import { html, css } from 'lit-element';
// import { Chart } from '../Chart';
import { Chart } from 'chart.js';
import { BaseElement } from './BaseElement';
import { PatientProgressData } from '../PatientProgressData';
import { CPMSessionDay } from '../CPMSessionDaily';
import toUTCLocaleDateString from '../toUTCLocaleDateString';

const _data = Symbol();
const _resizeHandler = Symbol();
const _chart = Symbol();

Chart.platform.disableCSSInjection = true;

class ROMChart extends BaseElement {
	constructor() {
		super();

		this[_resizeHandler] = () => {
			if(this.chart) {
				this.chart.resize();
				this.chart.render();
			}
		};
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					overflow: hidden;
				}
				canvas {
					width: 100%;
					height: 100%;
				}
				p {
					text-align: center;
					border: var(--theme-border, black);
					border-radius: 0.25em;
					padding: 0.25em;
					margin: 0.25em;
				}
			`
		];
	}
	get canvas() {
		return this.shadowRoot.querySelector('canvas');
	}
	get chart() {
		return this[_chart];
	}
	get data() {
		return this[_data];
	}
	set data(v) {
		if(v !== null && !(v instanceof PatientProgressData))
			throw new TypeError('data must be an instance of PatientProgressData.');
		this[_data] = v;
		this.requestUpdate();
		setTimeout(() => this.drawChart(), 0);
	}
	firstUpdated() {
		super.firstUpdated();
		this.drawChart();
	}
	connectedCallback() {
		super.connectedCallback();

		this.window.addEventListener('resize', this[_resizeHandler]);
	}
	disconnectedCallback() {
		this.window.removeEventListener('resize', this[_resizeHandler]);

		super.disconnectedCallback();
	}
	drawChart() {
		if(!this.data || !this.data.lastDay)
			return;
		
		const cpmSessionDays = [...this.data.cpmSessionDaily];
		let allDays = [];
		let date = new Date(this.data.orderDetail.surgeryDate);
		while(date.getTime() <= this.data.lastDay) {
			const cpmSessionDay = cpmSessionDays.find(v => v.usageDate === date.getTime());
			allDays.push(cpmSessionDay || CPMSessionDay.fromObject({ usageDate: date }));
			date.setUTCDate(date.getUTCDate() + 1);
		}

		const datasets = [
			{
				label: 'Flexion°',
				data: allDays.map(v => v.maxFlexion),
				backgroundColor: [
					'rgba(0, 0, 0, 0.1)'
				],
				borderColor: [
					'rgba(0, 0, 255, 1)'
				],
				borderWidth: 2,
				fill: 1,
				spanGaps: false,
				pointBorderColor: 'rgba(0, 0, 255, 1)'
			},
			{
				label: 'Extension°',
				data: allDays.map(v => v.maxExtension),
				backgroundColor: [
					'rgba(0, 0, 0, 0.1)'
				],
				borderColor: [
					'rgba(0, 0, 255, 1)'
				],
				borderWidth: 2,
				fill: 0,
				spanGaps: false,
				pointBorderColor: 'rgba(0, 0, 255, 1)'
			}
		];
		if(this.data.doctorKneeProtocol) {
			datasets.push(
				{
					label: 'Flexion Goal',
					data: allDays.map(() => this.data.doctorKneeProtocol.targetFlexion),
					backgroundColor: [
						'rgba(0, 0, 0, 0)'
					],
					borderColor: [
						'rgba(100, 200, 100, 1)'
					],
					borderWidth: 2,
					fill: false
				},
				{
					label: 'Extension Goal',
					data: allDays.map(() => this.data.doctorKneeProtocol.targetExtension),
					backgroundColor: [
						'rgba(0, 0, 0, 0)'
					],
					borderColor: [
						'rgba(100, 200, 100, 1)'
					],
					borderWidth: 2,
					fill: false
				}
			);
		}
		
		this[_chart] = new Chart(this.canvas, {
			type: 'line',
			data: {
				labels: allDays.map(v => toUTCLocaleDateString(new Date(v.usageDate))),
				datasets
			},
			options: {
				tooltips: {
					bodyFontSize: 16,
					titleFontSize: 16
				},
				scales: {
					yAxes: [{
						ticks: {
							beginAtZero: false,
							fontSize: 16
						},
						
					}],
					xAxes: [
						{
							ticks: {
								fontSize: 16
							}
						}
					]
				},
				legend: {
					labels: {
						fontSize: 16
					}
				},
				hover: {
					animationDuration: 0
				},
				responsive: false,
				animation: {
					duration: 0
				}
			}
		});
	}
	render() {
		const showCanvas = this.data && this.data.cpmSessionDaily.lastDay;
		return html`
			<canvas class="${showCanvas ? '' : 'no-display'}"></canvas>
			<p class="${!showCanvas ? '' : 'no-display'}">
				No CPM session data available.
			</p>
		`;
	}
}

customElements.define('pp-rom-chart', ROMChart);